
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCashbackRules } from "@/hooks/useCashbackRules";
import { Loader2, Save, RotateCcw, AlertCircle, CheckCircle2, Percent, X, Clock } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { useCashbackToggle } from "@/hooks/useCashbackToggle";
import { useCashbackStatus } from "@/hooks/useCashbackStatus";

const CashbackRules = () => {
  const {
    formData,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    saveRule,
    resetForm,
    updateFormField
  } = useCashbackRules();

  const { toggleCashback, isToggling } = useCashbackToggle();
  const { data: statusData, isLoading: isLoadingStatus } = useCashbackStatus();
  const isEnabled = statusData?.isEnabled || false;

  const handleToggle = async (checked: boolean) => {
    await toggleCashback(checked);
  };

  const handleSave = async () => {
    if (!isEnabled) return;
    await saveRule();
  };

  if (isLoading || isLoadingStatus) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">Carregando regras...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controle do Cashback */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Controle do Cashback Padrão</CardTitle>
          <CardDescription>
            Ative ou desative o cashback padrão. Quando ativo, todas as compras processadas pelo N8N gerarão cashback.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-3 p-3 rounded-lg border bg-card">
            <div className="flex items-center space-x-2">
              {isEnabled ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-600" />
              )}
              <Label className="text-sm font-medium cursor-pointer">
                Cashback Padrão
              </Label>
            </div>
            
            <div className="flex-1 text-right">
              <span className={`text-xs px-2 py-1 rounded-full ${
                isEnabled 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                  : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
              }`}>
                {isEnabled ? 'Ativo' : 'Pausado'}
              </span>
            </div>
            
            <Switch
              checked={isEnabled}
              onCheckedChange={handleToggle}
              disabled={isToggling}
            />
          </div>
          
          {!isEnabled && (
            <Alert className="border-amber-200 bg-amber-50 mt-4">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Cashback Pausado:</strong> O sistema de cashback está desativado. 
                Nenhum cashback será gerado pelo N8N e as configurações abaixo estão disponíveis apenas para visualização.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {hasUnsavedChanges && isEnabled && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            Você tem alterações não salvas. Clique em "Salvar Configurações" para aplicar as mudanças.
          </AlertDescription>
        </Alert>
      )}

      {!hasUnsavedChanges && isEnabled && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <strong>Cashback Ativo:</strong> Suas regras estão salvas e o cashback padrão está ativo. 
            Todas as compras processadas pelo N8N usarão essas configurações.
          </AlertDescription>
        </Alert>
      )}

      <Card className={!isEnabled ? "opacity-60" : ""}>
        <CardHeader>
          <CardTitle>Configurações do Cashback</CardTitle>
          <CardDescription>
            Configure o percentual, multiplicador e dias de expiração do cashback padrão.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Layout com inputs na esquerda e exemplo na direita */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Inputs alinhados na esquerda */}
            <Card className="bg-muted/30">
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Percent className="w-4 h-4" />
                      Percentual
                    </Label>
                     <Input 
                       type="number" 
                       step="0.01"
                       min="0"
                       max="100"
                       placeholder="5.00" 
                       value={formData.percentage}
                       onChange={(e) => updateFormField('percentage', e.target.value)}
                       disabled={!isEnabled}
                       className="bg-white"
                     />
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <X className="w-4 h-4" />
                      Multiplicador
                    </Label>
                    <Select 
                      value={formData.multiplier} 
                      onValueChange={(value) => updateFormField('multiplier', value)}
                      disabled={!isEnabled}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1x</SelectItem>
                        <SelectItem value="2">2x</SelectItem>
                        <SelectItem value="3">3x</SelectItem>
                        <SelectItem value="4">4x</SelectItem>
                        <SelectItem value="5">5x</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Dias para Expirar
                    </Label>
                     <Input 
                       type="number" 
                       min="1"
                       placeholder="30" 
                       value={formData.expiration_days}
                       onChange={(e) => updateFormField('expiration_days', e.target.value)}
                       disabled={!isEnabled}
                       className="bg-white"
                     />
                  </div>

                  <div className="space-y-2">
                    <Label>Incluir Frete</Label>
                    <Select 
                      value={formData.include_shipping ? "true" : "false"} 
                      onValueChange={(value) => updateFormField('include_shipping', value === "true")}
                      disabled={!isEnabled}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Incluir</SelectItem>
                        <SelectItem value="false">Não incluir</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Exemplo de cálculo na direita */}
            <Card className="bg-muted/30">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <h4 className="font-semibold text-lg text-card-foreground mb-2">Exemplo de Cálculo</h4>
                  <p className="text-sm text-muted-foreground">
                    Simulação baseada nas configurações atuais
                  </p>
                </div>

                <div className="space-y-4 text-base">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Valor da compra:</span>
                    <span className="font-medium">R$ 100,00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cashback ({formData.percentage || "5"}%):</span>
                    <span className="text-primary font-medium">R$ {(100 * (parseFloat(formData.percentage || "5") / 100)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Multiplicador:</span>
                    <span className="font-medium">x{formData.multiplier || "3"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gasto Mínimo:</span>
                    <span className="font-medium">R$ {(100 * (parseFloat(formData.percentage || "5") / 100) * parseInt(formData.multiplier || "3")).toFixed(2)}</span>
                  </div>
                  <hr className="border-border my-4" />
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Métrica de retorno:</span>
                    <span className="text-primary">{(((100 * (parseFloat(formData.percentage || "5") / 100) * parseInt(formData.multiplier || "3")) - (100 * (parseFloat(formData.percentage || "5") / 100))) / (100 * (parseFloat(formData.percentage || "5") / 100)) * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="flex gap-3 pt-6 border-t">
            <Button 
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges || !isEnabled}
              className="flex items-center gap-2"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isSaving ? "Salvando..." : "Salvar Configurações"}
            </Button>
            
            {hasUnsavedChanges && isEnabled && (
              <Button 
                variant="outline" 
                onClick={resetForm}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Cancelar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CashbackRules;
